import { isApiRequestError, isConflictError, isRetryableError } from '../api/errors.js';
import {
  type OfflineDb,
  type QueuedWrite,
  type QueuedWriteError,
  type QueuedWriteRecord,
  type QueueableMethod,
  WRITE_QUEUE_STORE,
  openOfflineDb,
} from './db.js';

/**
 * An ordered, durable queue of writes the shop made while the connection was down.
 *
 * Order is the whole point. "Record ₹500 advance" then "mark order ready" must reach the
 * server in that sequence or the shop's ledger tells a different story than the shop does.
 * So the flush is strictly serial and stops at the first write that could not be delivered —
 * a later write is never allowed to overtake an earlier one.
 *
 * A write the SERVER rejected is different from one that could not be delivered. A 4xx will
 * fail identically forever, so it is parked as `failed` with its reason kept, the queue moves
 * on, and the owner is shown what happened (a 409 is surfaced as a conflict — someone else
 * changed the record first). Nothing is ever silently discarded.
 */

export interface EnqueueInput {
  method: QueueableMethod;
  path: string;
  body?: unknown;
  /**
   * Reuse the idempotency key of a direct attempt that died mid-flight, so the retry the
   * queue makes cannot apply the same write twice.
   */
  id?: string;
  /** i18n key describing the write, e.g. `queue.item.recordPayment`. */
  labelKey: string;
  labelParams?: Record<string, string | number>;
  invalidate?: string[][];
}

export interface FlushResult {
  /** Writes accepted by the server and removed from the queue. */
  sent: number;
  /** Writes the server rejected. They stay in the queue, marked `failed`. */
  rejected: number;
  /** True when the flush stopped early because the connection is still down. */
  interrupted: boolean;
}

export interface WriteQueueSnapshot {
  pending: readonly QueuedWrite[];
  failed: readonly QueuedWrite[];
  pendingCount: number;
  failedCount: number;
  flushing: boolean;
}

const EMPTY_SNAPSHOT: WriteQueueSnapshot = Object.freeze({
  pending: Object.freeze([]),
  failed: Object.freeze([]),
  pendingCount: 0,
  failedCount: 0,
  flushing: false,
});

export interface WriteQueueOptions {
  /** Delivers one queued write. Rejects with the same error shapes the API client throws. */
  send: (write: QueuedWrite) => Promise<unknown>;
  /** Injected in tests. */
  openDb?: () => Promise<OfflineDb>;
  now?: () => number;
  createId?: () => string;
  /** Called after a write is accepted, so caches can be invalidated. */
  onSent?: (write: QueuedWrite) => void;
  /** Called when the server rejected a write, so the owner can be told. */
  onRejected?: (write: QueuedWrite, error: unknown) => void;
}

function randomId(): string {
  const cryptoRef = globalThis.crypto as Crypto | undefined;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') return cryptoRef.randomUUID();
  return `w_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function describeError(error: unknown): QueuedWriteError {
  if (isApiRequestError(error)) {
    return {
      status: error.status,
      code: error.code,
      message: error.message,
      conflict: isConflictError(error),
    };
  }
  return {
    message: error instanceof Error ? error.message : String(error),
    conflict: false,
  };
}

export class WriteQueue {
  readonly #options: WriteQueueOptions;
  readonly #listeners = new Set<() => void>();
  #dbPromise: Promise<OfflineDb> | null = null;
  #flushPromise: Promise<FlushResult> | null = null;
  #snapshot: WriteQueueSnapshot = EMPTY_SNAPSHOT;

  constructor(options: WriteQueueOptions) {
    this.#options = options;
  }

  #db(): Promise<OfflineDb> {
    this.#dbPromise ??= (this.#options.openDb ?? openOfflineDb)();
    return this.#dbPromise;
  }

  /** Snapshot for `useSyncExternalStore`. Stable between changes. */
  getSnapshot(): WriteQueueSnapshot {
    return this.#snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }

  #setSnapshot(next: Partial<WriteQueueSnapshot>): void {
    this.#snapshot = { ...this.#snapshot, ...next };
    this.#emit();
  }

  /**
   * Read every queued write, oldest first. `getAllKeys` and `getAll` both come back in
   * ascending key order, and the key IS the sequence number, so index `i` of one lines up
   * with index `i` of the other.
   */
  async list(): Promise<QueuedWrite[]> {
    const db = await this.#db();
    const [keys, records] = await Promise.all([
      db.getAllKeys(WRITE_QUEUE_STORE),
      db.getAll(WRITE_QUEUE_STORE),
    ]);

    const writes: QueuedWrite[] = [];
    for (const [index, record] of records.entries()) {
      const seq = keys[index];
      if (seq === undefined) continue;
      writes.push({ ...record, seq: Number(seq) });
    }
    return writes.sort((a, b) => a.seq - b.seq);
  }

  /** Re-read the store and publish a fresh snapshot. */
  async refresh(): Promise<WriteQueueSnapshot> {
    const all = await this.list();
    const pending = all.filter((write) => write.status === 'pending');
    const failed = all.filter((write) => write.status === 'failed');
    this.#setSnapshot({
      pending,
      failed,
      pendingCount: pending.length,
      failedCount: failed.length,
    });
    return this.#snapshot;
  }

  /** Add a write to the back of the queue. It will be sent after everything already waiting. */
  async enqueue(input: EnqueueInput): Promise<QueuedWrite> {
    const now = this.#options.now ?? Date.now;
    const createId = this.#options.createId ?? randomId;

    const record: QueuedWriteRecord = {
      id: input.id ?? createId(),
      createdAt: now(),
      method: input.method,
      path: input.path,
      ...(input.body === undefined ? {} : { body: input.body }),
      labelKey: input.labelKey,
      ...(input.labelParams ? { labelParams: input.labelParams } : {}),
      ...(input.invalidate ? { invalidate: input.invalidate } : {}),
      attempts: 0,
      status: 'pending',
    };

    const db = await this.#db();
    const seq = Number(await db.add(WRITE_QUEUE_STORE, record));
    await this.refresh();
    return { ...record, seq };
  }

  /** Remove a write for good — the owner has decided it is not worth retrying. */
  async discard(seq: number): Promise<void> {
    const db = await this.#db();
    await db.delete(WRITE_QUEUE_STORE, seq);
    await this.refresh();
  }

  /** Move a rejected write back into the pending queue, at its original position. */
  async retry(seq: number): Promise<void> {
    const db = await this.#db();
    const record = await db.get(WRITE_QUEUE_STORE, seq);
    if (!record) return;
    const { lastError: _dropped, ...rest } = record;
    await db.put(WRITE_QUEUE_STORE, { ...rest, status: 'pending' }, seq);
    await this.refresh();
  }

  async clear(): Promise<void> {
    const db = await this.#db();
    await db.clear(WRITE_QUEUE_STORE);
    await this.refresh();
  }

  /**
   * Send everything waiting, in submission order. Concurrent callers share one flush — the
   * reconnect event and a manual "retry now" tap must not double-send.
   */
  flush(): Promise<FlushResult> {
    this.#flushPromise ??= this.#runFlush().finally(() => {
      this.#flushPromise = null;
      this.#setSnapshot({ flushing: false });
    });
    return this.#flushPromise;
  }

  async #runFlush(): Promise<FlushResult> {
    this.#setSnapshot({ flushing: true });
    const result: FlushResult = { sent: 0, rejected: 0, interrupted: false };

    const queued = (await this.list()).filter((write) => write.status === 'pending');

    for (const write of queued) {
      try {
        await this.#options.send(write);
        await this.#remove(write.seq);
        result.sent += 1;
        this.#options.onSent?.(write);
      } catch (error) {
        if (isRetryableError(error)) {
          // Still unreachable. Leave this write — and everything behind it — exactly where
          // it is, so the order the shop entered them in is preserved.
          await this.#update(write.seq, { attempts: write.attempts + 1 });
          result.interrupted = true;
          break;
        }
        await this.#update(write.seq, {
          attempts: write.attempts + 1,
          status: 'failed',
          lastError: describeError(error),
        });
        result.rejected += 1;
        this.#options.onRejected?.(write, error);
      }
    }

    await this.refresh();
    return result;
  }

  async #remove(seq: number): Promise<void> {
    const db = await this.#db();
    await db.delete(WRITE_QUEUE_STORE, seq);
  }

  async #update(seq: number, patch: Partial<QueuedWriteRecord>): Promise<void> {
    const db = await this.#db();
    const record = await db.get(WRITE_QUEUE_STORE, seq);
    if (!record) return;
    await db.put(WRITE_QUEUE_STORE, { ...record, ...patch }, seq);
  }
}
